import { useOutletContext, useRouteLoaderData } from '@remix-run/react';
import { CardElement, PaymentRequestButtonElement, useElements, useStripe } from '@stripe/react-stripe-js';
import cx from 'classnames';
import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientOnly } from 'remix-utils/client-only';

import useFormState from '~/pages/upgrade/hooks/useFormState';

import Divider from '~/components/Divider';
import { useWebThemeContext } from '~/components/Layout/Context/useWebThemeContext';
import Signup from '~/components/Signup';
import Typography from '~/components/Typography';

import type { OutletContext } from '~/interfaces/outlet_context';
import type { Publication } from '~/interfaces/publication';
import type { Price, PricingDetails } from '~/interfaces/subscriptions';
import type { UpgradePage } from '~/interfaces/upgrade_page';

import useEmail from '~/hooks/useEmail';
import useIsMobile from '~/hooks/useIsMobile';

import Button from '../../../components/Button';
import StatusInputs from '../../../components/Forms/StatusInputs';
import { usePricingContext } from '../_content/usePricingContext';
import useApplePay from '../hooks/useApplePay';
import useHandleCheckoutSubmissionStates from '../hooks/useHandleCheckoutSubmissionStates';
import useInternalCheckout from '../hooks/useInternalCheckout';
import currencyFormatter from '../utils/currencyFormatter';

type GiftFormData = {
  email: string;
  recipient_email: string;
  purchaser_name: string;
  message: string;
  start_at: string;
};

interface Props {
  priceId: string;
  pricingDetails: PricingDetails;
  upgradePage: UpgradePage;
  publication: Publication;
  onContinue: () => void;
  premiumOfferId?: string;
  disabled?: boolean;
  giftForm?: GiftFormData;
}

const getCTAText = (price: Price, donationAmountCents: number, t: TFunction<'translation', undefined>) => {
  const isDonation = price?.interval === 'donation';
  const trialString = () => {
    if (
      typeof price?.premium_offer?.trial_period_days !== 'number' ||
      price?.premium_offer?.trial_period_days === 0 ||
      !price.denominator
    ) {
      return '';
    }

    return `/${price?.denominator} ${t('upgrade.free_trial.after_trial')}`;
  };

  return `${currencyFormatter({ currency: price?.currency }).format(
    ((isDonation ? donationAmountCents : price?.amount_cents) || 0) / 100,
  )}${trialString()}`;
};

const getCTAHelperText = (price: Price, t: TFunction<'translation', undefined>) => {
  if (typeof price?.premium_offer?.trial_period_days !== 'number' || price?.premium_offer?.trial_period_days === 0) {
    return '';
  }

  return t('upgrade.free_trial.nothing_due_today');
};

const UpgradeForm = ({
  priceId,
  premiumOfferId,
  upgradePage,
  pricingDetails,
  onContinue,
  publication,
  disabled,
  giftForm,
}: Props) => {
  const { signupWidgetConfigs } = useRouteLoaderData<any>('root');
  const { classes } = useWebThemeContext();
  const { setCookie, cookies } = useOutletContext<OutletContext>();
  const [email, setEmail] = useEmail();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [hasRedirected, setHasRedirected] = useState(false);
  const [stripeInputSelected, setStripeInputSelected] = useState(true);
  const [emailInputSelected, setEmailInputSelected] = useState(false);
  const [taxIdInputSelected, setTaxIdInputSelected] = useState(false);

  // It's an edge case, but occasionally a user might land on this page without signing up/upgrade at the same time.
  // This state allows us to handle for that case.
  const [fallbackEmail, setFallbackEmail] = useState('');

  const price = pricingDetails.tiers
    .flatMap((tier) => tier.prices)
    .find((price) => {
      return price.id === priceId;
    }) as Price;

  const fontFamily = upgradePage.main_section.card_font_family;
  const fontColor = upgradePage.main_section.headline_color;
  const ctaBgColor = upgradePage.main_section.checkout_cta_bg_color;
  const ctaTextColor = upgradePage.main_section.checkout_cta_text_color;
  const ctaCustomText = upgradePage.main_section.checkout_cta_text;
  const isFreePlan = priceId === 'free';
  const { donationAmountCents } = usePricingContext();

  const ctaText = ctaCustomText || `${t('stripe_checkout.pay')} ${getCTAText(price, donationAmountCents, t)}`;
  const ctaHelperText = getCTAHelperText(price, t);

  const showTaxIdField = !!publication?.subscriber_tax_id_collection_enabled;

  const stripe = useStripe();
  const elements = useElements();

  const { fetcher, data, isLoading, isSuccessful, isFailed } = useFormState();
  const giftEmail = giftForm?.email;

  const evaluatedEmail = giftEmail || email || fallbackEmail;

  // Test Cards for local and staging
  // Successful Visa: 4242 4242 4242 4242
  // 3DSecure Card: 4000 0000 0000 3063
  const {
    handleTaxIdChange,
    taxIdValue,
    taxIdIsValid,
    handleSubmit,
    submissionTriggered,
    handleRemoveDisabledState,
    handleDonationAmountCentsChanged: handleInternalDonationAmountCentsChanged,
  } = useInternalCheckout({
    stripe,
    elements,
    email: evaluatedEmail,
    fetcher,
    setEmail,
  });

  const { paymentRequest, applePayEvent, setApplePayEvent, onPaymentSubmit } = useApplePay({
    donationAmountCents,
    priceId,
    stripe,
    pricingDetails,
    setEmail,
    premiumOfferId,
    email: evaluatedEmail,
    cookies,
    fetcher,
    shouldCreatePaymentRequest: publication?.stripe_payment_method_domain_enabled,
  });

  useEffect(() => {
    handleInternalDonationAmountCentsChanged(String(donationAmountCents));
  }, [donationAmountCents]);

  // Some users are reporting that the email field is disabled and populated with a
  // default value of "false"
  // this mitigates the bug as we have been unable to recreate it
  // https://beehiivhelp.zendesk.com/agent/tickets/120061
  useEffect(() => {
    if ((typeof fallbackEmail === 'boolean' && fallbackEmail === false) || fallbackEmail === 'false') {
      setFallbackEmail('');
    }
  }, [fallbackEmail]);

  const { isProcessing3DSecure } = useHandleCheckoutSubmissionStates({
    stripe,
    elements,
    isSuccessful,
    isFailed,
    data,
    hasRedirected,
    applePayEvent,
    setApplePayEvent,
    setCookie,
    setHasRedirected,
    onContinue,
    handleRemoveDisabledState,
    isGift: !!giftForm,
  });

  const isFormDisabled = !priceId;
  const isFormBusy = isProcessing3DSecure || submissionTriggered;
  const [isCardDetailsFilled, setIsCardDetailsFilled] = useState(false);
  const giftFormCardDetailsFilled = () => {
    // The previous behavior was to not disable the submit button even if no card details were filled.
    // To prevent breaking changes, we'll keep the same behavior unless the gift form is present.
    if (!giftForm) return true;

    return isCardDetailsFilled;
  };

  const canFormSubmit = !isFormDisabled && !isFormBusy && !disabled && giftFormCardDetailsFilled();

  if (isFreePlan && !email && !giftEmail) {
    return (
      <div className="relative w-full max-w-md">
        <Signup
          signupStyles={{
            ...signupWidgetConfigs,
            buttonBgColor: ctaBgColor,
            buttonTextColor: ctaTextColor,
            fontFamily,
          }}
        />
      </div>
    );
  }

  if (isFreePlan) {
    return (
      <div className="relative w-full max-w-sm">
        <Button
          type="submit"
          variant="primary"
          className="flex w-full justify-center"
          isLoading={isLoading}
          onClick={onContinue}
          style={{
            backgroundColor: ctaBgColor,
            border: `1px solid ${ctaBgColor}`,
            color: ctaTextColor,
          }}
        >
          {t('stripe_checkout.free_reader')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-sm flex-col gap-y-2">
      <fetcher.Form
        method="patch"
        action="/upgrade"
        className="relative flex w-full max-w-sm flex-col gap-y-2"
        onSubmit={handleSubmit}
      >
        <ClientOnly>
          {() => {
            return !email && !giftEmail ? (
              <input
                type="email"
                name="email"
                autoComplete="email"
                value={fallbackEmail}
                onFocus={() => setEmailInputSelected(true)}
                onBlur={() => setEmailInputSelected(false)}
                required
                disabled={false}
                onChange={(e) => setFallbackEmail(e.target.value)}
                placeholder="john@doe.com"
                className={cx(classes.radius, 'w-full max-w-sm bg-white p-2')}
                style={{
                  fontFamily,
                  color: '#111827',
                  border: emailInputSelected ? `1px solid ${ctaBgColor}` : '1px solid #e5e7eb',
                }}
              />
            ) : (
              <input type="hidden" name="email" value={giftEmail || email} />
            );
          }}
        </ClientOnly>
        {showTaxIdField && (
          <input
            type="text"
            name="tax_id"
            value={taxIdValue}
            onFocus={() => setTaxIdInputSelected(true)}
            onBlur={() => setTaxIdInputSelected(false)}
            onChange={(e) => handleTaxIdChange(e.target.value)}
            placeholder={t('upgrade.tax_id_placeholder')}
            className={cx(classes.radius, 'w-full max-w-sm bg-white p-2')}
            style={{
              fontFamily,
              color: '#111827',
              border: '1px solid',
              borderColor: taxIdInputSelected ? (taxIdIsValid ? ctaBgColor : 'red') : '#e5e7eb',
            }}
          />
        )}
        <input type="hidden" name="force_three_d_secure" value={'false'} />
        <input type="hidden" name="price_id" value={priceId} />
        <input type="hidden" name="premium_offer_id" value={premiumOfferId} />
        <input hidden name="last_resource_guid" defaultValue={cookies.last_resource_guid} />
        {/* Gift Form Fields */}
        {giftForm && (
          <>
            <input type="hidden" name="isGift" value="true" />
            <input type="hidden" name="purchaser_name" value={giftForm.purchaser_name} />
            <input type="hidden" name="recipient_email" value={giftForm.recipient_email} />
            <input type="hidden" name="message" value={giftForm.message} />
            <input type="hidden" name="start_at" value={giftForm.start_at} />
          </>
        )}
        <StatusInputs name="upgrade" errorMessage={t('errors.default')} successMessage={t('upgrade.success.message')} />
        <div
          style={{
            border: stripeInputSelected ? `1px solid ${ctaBgColor}` : '1px solid #e5e7eb',
          }}
          className={cx(classes.radius, 'w-full max-w-sm border bg-white p-2', paymentRequest && 'mt-4')}
        >
          <CardElement
            onFocus={() => setStripeInputSelected(true)}
            onBlur={() => setStripeInputSelected(false)}
            onChange={(e) => {
              if (e.complete) setIsCardDetailsFilled(true);
            }}
            options={{
              ...(isMobile && { disableLink: true }),
              style: {
                base: {
                  fontSize: '16px',
                  color: '#111827',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
              },
            }}
          />
        </div>
        <div>
          <Button
            type="submit"
            variant="primary"
            className="flex w-full justify-center"
            disabled={!canFormSubmit}
            style={{
              backgroundColor: canFormSubmit ? ctaBgColor : '#ccc',
              border: canFormSubmit ? `1px solid ${ctaBgColor}` : '1px solid #ccc',
              color: ctaTextColor,
            }}
          >
            {isFormBusy ? `${t('shared.actions.submitting')}...` : ctaText}
          </Button>
          {ctaHelperText ? (
            <div className="mt-2 w-full justify-center text-center">
              <Typography weight="light" size="sm" color={fontColor}>
                {ctaHelperText}
              </Typography>
            </div>
          ) : null}
          <div className="w-full justify-center text-center">
            <span
              className="text-xs"
              style={{
                color: fontColor,
              }}
            >
              {t('shared.cta.powered_by')}{' '}
              <a className="underline" href="https://stripe.com/" target="_blank" rel="noreferrer noopener">
                Stripe
              </a>
              .
            </span>
          </div>
        </div>
      </fetcher.Form>
      {paymentRequest && (
        <>
          <Divider label={t('shared.divider_label')} />
          <div
            className="w-full max-w-sm"
            onClick={!evaluatedEmail ? onPaymentSubmit : undefined}
            onKeyDown={!evaluatedEmail ? onPaymentSubmit : undefined}
            onKeyUp={!evaluatedEmail ? onPaymentSubmit : undefined}
          >
            <PaymentRequestButtonElement
              className={cx(!evaluatedEmail && 'pointer-events-none')}
              options={{ paymentRequest }}
              onClick={onPaymentSubmit}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default UpgradeForm;
